/**
 * invoicePdfGenerator.ts
 *
 * Génère un PDF de facture côté client en rendant le même template HTML
 * que celui utilisé par CreateInvoice.tsx.
 * Utilisé à la fois pour :
 *   - Le téléchargement depuis l'historique (InvoiceHistory.tsx)
 *   - L'envoi par email (CreateInvoice.tsx via generatePreviewPdfBase64)
 */

import { toPng } from 'html-to-image';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { exigerMentionsLegales } from '../../lib/mentionsLegales';
import { CSS_TAILLE_TEXTE_FIXE, logoEnDataUri, capturerAvecLogo } from '../../lib/pdfTelephone';

const fmt = (val: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
        Number.isFinite(val) ? val : 0
    );

const toDisplayDate = (rawDate: string) => {
    if (!rawDate) return '--/--/----';
    // Handles both "YYYY-MM-DD" and ISO strings
    const d = new Date(rawDate);
    if (isNaN(d.getTime())) return rawDate;
    return d.toLocaleDateString('fr-FR');
};

const DEFAULT_ACCENT = '#2f9e9e';

/**
 * Lit les dimensions réelles d'un PNG encodé en data URI directement depuis l'en-tête
 * binaire (chunk IHDR, octets 16-23), sans dépendre du chargement DOM d'une <img>.
 * Nécessaire car le moteur de capture (html-to-image) ne respecte pas toujours
 * fiablement max-height/max-width + object-fit sur des logos au ratio très large.
 */
const getPngNaturalSize = (dataUrl: string): { width: number; height: number } | null => {
    try {
        if (!dataUrl.startsWith('data:image/png')) return null;
        const base64 = dataUrl.split(',')[1];
        if (!base64) return null;
        const binary = atob(base64.slice(0, 40)); // l'IHDR est toujours dans les premiers octets
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const view = new DataView(bytes.buffer);
        const width = view.getUint32(16, false);
        const height = view.getUint32(20, false);
        if (!width || !height) return null;
        return { width, height };
    } catch {
        return null;
    }
};

/** Calcule une taille d'affichage bornée (comme object-fit:contain) mais en pixels explicites. */
const computeBoundedLogoSize = (dataUrl: string, maxWidth: number, maxHeight: number) => {
    const natural = getPngNaturalSize(dataUrl);
    if (!natural) return null;
    const ratio = natural.width / natural.height;
    let width = maxWidth;
    let height = maxWidth / ratio;
    if (height > maxHeight) {
        height = maxHeight;
        width = maxHeight * ratio;
    }
    return { width: Math.round(width), height: Math.round(height) };
};

/** Builds the HTML string of the invoice, matching the CreateInvoice preview exactly. */
export function buildInvoiceHtml(params: {
    invoice: any;
    payload: any;
    snapshot: any;
    accentColor?: string;
}): string {
    const { invoice, payload, snapshot } = params;
    const accent = params.accentColor || snapshot.primary_color || DEFAULT_ACCENT;

    const companyName = snapshot.company_name || 'Nom de l\'établissement';
    const invoiceNumber = invoice.invoice_number || payload.invoiceNumber || 'F-XXXXXX';
    const invoiceDate = toDisplayDate(payload.invoiceDate || invoice.due_date || invoice.created_at || '');
    const logoUrl = snapshot.logo_url ? resolveLogoUrl(snapshot.logo_url) : null;
    const logoSize = logoUrl ? computeBoundedLogoSize(logoUrl, 250, 112) : null;
    const logoStyle = logoSize
        ? `width:${logoSize.width}px;height:${logoSize.height}px;margin-bottom:8px;align-self:flex-start;`
        : 'max-height:112px;max-width:250px;margin-bottom:8px;object-fit:contain;align-self:flex-start;';

    const clientName = payload.clientName || invoice.customer_name || '';
    const clientAddress = payload.clientAddress || '';
    const clientPostalCode = payload.clientPostalCode || '';
    const clientCity = payload.clientCity || '';
    const clientCountry = payload.clientCountry || '';
    const coverCount = payload.coverCount || '';

    const lines: any[] = Array.isArray(payload.lines) ? payload.lines : [];
    const tvaRates: number[] = Array.isArray(snapshot.tva_rates) && snapshot.tva_rates.length > 0
        ? snapshot.tva_rates.map(Number)
        : [20];

    // Compute per-line summaries
    const lineSummaries = lines.map((line: any) => {
        const rateDetails = tvaRates.map((rate) => {
            const ttc = Number(line.ttcByRate?.[rate] ?? 0);
            const ht = ttc / (1 + rate / 100);
            const tva = ttc - ht;
            return { rate, ttc, ht, tva };
        });
        return {
            ...line,
            rateDetails,
            totalHt: rateDetails.reduce((s, d) => s + d.ht, 0),
            totalTva: rateDetails.reduce((s, d) => s + d.tva, 0),
            totalTtc: rateDetails.reduce((s, d) => s + d.ttc, 0),
        };
    });

    const totalHt = Number(invoice.total_ht ?? lineSummaries.reduce((s, l) => s + l.totalHt, 0));
    const totalTva = Number(invoice.total_tva ?? lineSummaries.reduce((s, l) => s + l.totalTva, 0));
    const totalTtcBrut = Number(invoice.total_ttc ?? lineSummaries.reduce((s, l) => s + l.totalTtc, 0));
    const alreadyPaid = Number(invoice.already_paid ?? payload.amountAlreadyPaid ?? 0);
    const netToPay = Number(invoice.remaining_due ?? Math.max(0, totalTtcBrut - alreadyPaid));
    const isSettled = totalTtcBrut > 0 && alreadyPaid >= totalTtcBrut;
    // Facture d'avoir (remboursement) : montants négatifs, renvoi à la facture d'origine, aucun solde à payer.
    const isCredit = payload.documentType === 'avoir';

    // Date à laquelle le règlement doit intervenir (mention obligatoire), même calcul que InvoiceDocument.tsx.
    const echeanceDate = (() => {
        if (isSettled || isCredit) return '';
        const rawDate = payload.invoiceDate || invoice.due_date || invoice.created_at || '';
        if (!rawDate) return '';
        const delayDays = Number.isFinite(Number(snapshot.payment_delay_days)) ? Number(snapshot.payment_delay_days) : 30;
        const d = new Date(rawDate);
        if (isNaN(d.getTime())) return '';
        d.setDate(d.getDate() + delayDays);
        return d.toLocaleDateString('fr-FR');
    })();
    // Le libellé/montant du bloc final s'adapte à la situation réelle de paiement : payé intégralement,
    // partiellement (reste dû), ou pas du tout réglé.
    const bottomLabel = isCredit ? 'MONTANT REMBOURSÉ' : (isSettled ? 'TOTAL RÉGLÉ' : (alreadyPaid > 0 ? 'RESTE À PAYER' : 'TOTAL À PAYER'));
    const bottomAmount = isCredit ? totalTtcBrut : (isSettled ? totalTtcBrut : netToPay);
    const bottomColor = (isSettled || isCredit) ? accent : '#dc2626';
    // Ventilation par moyen de paiement (ex: 20€ en espèces + le reste en virement) — n'a de sens
    // qu'une fois la facture soldée ; sinon on garde l'affichage "Déjà réglé" existant pour un acompte.
    const paymentBreakdown: { method: string; amount: number }[] = Array.isArray(payload.paymentBreakdown) ? payload.paymentBreakdown : [];
    const paymentMethodLines = isSettled
        ? (paymentBreakdown.length > 1
            ? paymentBreakdown.map((entry) => `
        <div style="display:flex;justify-content:space-between;font-size:10px;color:#64748b;margin-top:4px;">
          <span>${entry.method} :</span>
          <span>${fmt(entry.amount)}</span>
        </div>`).join('')
            : (paymentBreakdown[0]?.method || payload.paymentMethod) ? `
        <div style="display:flex;justify-content:space-between;font-size:10px;color:#64748b;margin-top:4px;">
          <span>Moyen de paiement :</span>
          <span>${paymentBreakdown[0]?.method || payload.paymentMethod}</span>
        </div>` : '')
        : '';

    const activeTvaRates = tvaRates.filter((rate) =>
        lineSummaries.some((l) => l.rateDetails.some((d: any) => d.rate === rate && d.ht !== 0))
    );
    const displayTvaRates = activeTvaRates.length > 0 ? activeTvaRates : tvaRates;
    const hasContent = lineSummaries.some((l) => l.totalTtc !== 0);

    const clientHasAddress = clientName || clientAddress || clientCity || clientPostalCode;

    const legalParts1: string[] = [];
    if (snapshot.rcs_ville && snapshot.rcs_numero) legalParts1.push(`RCS ${snapshot.rcs_ville} ${snapshot.rcs_numero}`);
    if (snapshot.siret) legalParts1.push(`• SIRET ${snapshot.siret}`);
    // Régime de TVA figé dans la photo prise à l'émission : une facture ancienne garde la mention qui
    // était la bonne ce jour-là. En franchise en base, la mention d'exonération remplace le numéro de TVA.
    if (snapshot.tva_regime === 'franchise_293b') legalParts1.push('• TVA non applicable, art. 293 B du CGI');
    else if (snapshot.tva) legalParts1.push(`• TVA ${snapshot.tva}`);
    if (snapshot.tva_regime !== 'franchise_293b' && snapshot.tva_on_debits) legalParts1.push("• TVA acquittée d'après les débits");
    if (snapshot.ape) legalParts1.push(`• Code APE ${snapshot.ape}`);
    // Coordonnées bancaires : uniquement quand le règlement attendu est un virement (choisi par
    // l'établissement à la souscription de la formule ou à la clôture de la prestation).
    if (payload.expectedPaymentMethod === 'Virement' && snapshot.iban) {
        const ibanFormate = String(snapshot.iban).replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
        legalParts1.push(`• Paiement par virement — IBAN ${ibanFormate}${snapshot.bic ? ` • BIC ${snapshot.bic}` : ''}`);
    }

    const legalParts2: string[] = [];
    if (snapshot.capital) legalParts2.push(`Capital social : ${snapshot.capital}`);
    if (snapshot.siege_social) legalParts2.push(`• ${snapshot.siege_social}`);
    else if (snapshot.address || snapshot.city) {
        const addr = [snapshot.address, snapshot.postal_code, snapshot.city].filter(Boolean).join(' ');
        if (addr) legalParts2.push(`• ${addr}`);
    }

    // Mentions obligatoires (Code de commerce, art. L441-10), mêmes règles que InvoiceDocument.tsx (aperçu
    // à l'écran) : ce gabarit HTML est un template indépendant (pas le même composant), donc toute mention
    // ajoutée là-bas doit être répétée ici sous peine de diverger entre aperçu et PDF téléchargé/envoyé.
    const isProfessionalClient = payload.clientType === 'PROFESSIONNEL';
    const OPERATION_NATURE_LABELS: Record<string, string> = {
        services: 'Prestation de services',
        biens: 'Livraison de biens',
        mixte: 'Livraison de biens et prestation de services',
    };
    const operationNatureLabel = payload.isPrestation && payload.operationNature ? OPERATION_NATURE_LABELS[payload.operationNature] : '';
    const legalParts3: string[] = ['Escompte pour paiement anticipé : néant', "• Pénalités de retard : taux d'intérêt de la BCE majoré de 10 points, exigibles sans rappel"];
    if (isProfessionalClient) legalParts3.push('• Indemnité forfaitaire pour frais de recouvrement : 40 €');
    // Client particulier : le médiateur de la consommation de l'établissement (réglage unique, repris aussi dans les CGV, le devis et le contrat).
    else if (snapshot.cgv_mediator) legalParts3.push(`• Médiateur de la consommation : ${String(snapshot.cgv_mediator).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}`);

    // Au-dela d'un taux de TVA, le tableau porte assez de colonnes pour déborder la largeur d'une A4 :
    // on resserre alors les corps de texte et les marges internes. Mesuré, pas estimé.
    const compact = displayTvaRates.length > 1;
    const tailleLibelle = compact ? 14 : 18;
    const tailleChiffre = compact ? 11 : 14;
    const tailleTotal = compact ? 13 : 18;
    const tailleTotalHt = compact ? 12 : 16;
    const padV = compact ? 10 : 16;

    const lineRows = hasContent
        ? lineSummaries.map((line) => {
            const baseCols = displayTvaRates.map((rate) => {
                const detail = line.rateDetails.find((d: any) => d.rate === rate);
                const val = detail && detail.ht > 0 ? fmt(detail.ht) : '-';
                return `<td style="padding:${padV}px 0 ${padV}px 8px;text-align:right;color:#4a5568;font-size:${tailleChiffre}px;">${val}</td>`;
            }).join('');
            return `
            <tr>
              <td style="padding:${padV}px 0;">
                <p style="font-weight:700;color:#1e293b;font-size:${tailleLibelle}px;margin:0 0 2px 0;">${line.label || 'Prestation non renseignée'}</p>
                ${snapshot.enable_cover_count && coverCount ? `<p style="font-size:9px;color:#94a3b8;text-transform:uppercase;font-weight:700;letter-spacing:0.06em;margin:0;">${coverCount} couverts</p>` : ''}
              </td>
              <td style="padding:${padV}px 0 ${padV}px 8px;text-align:center;color:#4a5568;font-size:${tailleChiffre}px;">${line.quantity}</td>
              <td style="padding:${padV}px 0 ${padV}px 8px;text-align:right;color:#4a5568;font-size:${tailleChiffre}px;">${Number(line.quantity) > 0 ? fmt(line.totalHt / Number(line.quantity)) : '-'}</td>
              <td style="padding:${padV}px 0 ${padV}px 8px;text-align:right;color:#4a5568;font-size:${tailleChiffre}px;">${Number(line.quantity) > 0 ? fmt(line.totalTtc / Number(line.quantity)) : '-'}</td>
              ${baseCols}
              <td style="padding:${padV}px 0 ${padV}px 12px;text-align:right;font-weight:700;color:#475569;font-size:${tailleTotalHt}px;">${fmt(line.totalHt)}</td>
              <td style="padding:${padV}px 0 ${padV}px 12px;text-align:right;font-weight:900;color:#0f172a;font-size:${tailleTotal}px;">${fmt(line.totalTtc)}</td>
            </tr>`;
        }).join('')
        : `<tr><td colspan="${displayTvaRates.length + 6}" style="padding:32px;text-align:center;color:#cbd5e1;font-style:italic;font-size:14px;">Aucune prestation renseignée</td></tr>`;

    const tvaRows = displayTvaRates.map((rate) => {
        const rateTotal = lineSummaries.reduce((sum, line) => {
            const detail = line.rateDetails.find((d: any) => d.rate === rate);
            return sum + (detail?.tva || 0);
        }, 0);
        if (rateTotal <= 0) return '';
        return `
        <div style="display:flex;justify-content:space-between;font-size:10px;color:#94a3b8;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">
          <span>TVA collectée (${rate}%) :</span>
          <span style="color:#475569;">${fmt(rateTotal)}</span>
        </div>`;
    }).join('');

    const headerCols = displayTvaRates.map((rate) =>
        `<th style="padding-bottom:8px;padding-left:8px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;text-align:right;">Base HT (${rate}%)</th>`
    ).join('');

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8"/>
<title>${invoiceNumber}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  ${CSS_TAILLE_TEXTE_FIXE}
  body { font-family: Arial, Helvetica, sans-serif; background: #fff; color: #1e293b; }
  .invoice-wrap {
    background: white;
    padding: 8mm;
    width: 210mm;
    display: flex;
    flex-direction: column;
    position: relative;
  }
</style>
</head>
<body>
<div class="invoice-wrap">

  <!-- HEADER -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;gap:24px;">
    <div style="display:flex;flex-direction:column;min-width:0;">
      ${logoUrl
          ? `<img src="${logoUrl}" alt="Logo" style="${logoStyle}" />`
          : `<div style="font-size:11px;font-weight:900;letter-spacing:0.2em;color:#94a3b8;margin-bottom:8px;">LOGO</div>`
      }
      <h1 style="font-size:20px;font-weight:700;color:#1e293b;">${companyName}</h1>
      <div style="color:#475569;font-size:11px;margin-top:4px;line-height:1.4;">
        <p>${snapshot.address || '-'}</p>
        <p>${[snapshot.postal_code, snapshot.city].filter(Boolean).join(' ') || '-'}</p>
      </div>
      ${snapshot.phone ? `<p style="margin-top:4px;font-size:11px;color:#475569;font-weight:600;">Tél : ${snapshot.phone}</p>` : ''}
    </div>

    <div style="text-align:right;flex-shrink:0;">
      <h2 style="font-size:32px;font-weight:900;text-transform:uppercase;color:${accent};letter-spacing:-0.035em;margin-bottom:8px;">${isCredit ? 'FACTURE D\'AVOIR' : 'FACTURE'}</h2>
      <div style="background:#f8fafc;padding:16px;border-radius:12px;border:1px solid #f1f5f9;display:inline-block;text-align:left;min-width:190px;box-shadow:0 1px 3px rgba(0,0,0,0.06);">
        <div style="margin-bottom:8px;">
          <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;margin-bottom:2px;">${isCredit ? 'Numéro d\'avoir' : 'Numéro de facture'}</p>
          <p style="font-family:monospace;font-size:15px;font-weight:700;color:#0f172a;">${invoiceNumber}</p>
          ${isCredit && payload.originalInvoiceNumber ? `<p style="margin-top:6px;font-size:10px;font-weight:700;color:#475569;">Avoir sur la facture n° ${payload.originalInvoiceNumber}${payload.originalInvoiceDate ? ` du ${toDisplayDate(payload.originalInvoiceDate)}` : ''}</p>` : ''}
        </div>
        <div style="display:grid;grid-template-columns:${snapshot.enable_cover_count && coverCount || echeanceDate ? '1fr 1fr' : '1fr'};gap:12px;padding-top:8px;border-top:1px solid rgba(226,232,240,0.6);">
          <div>
            <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;margin-bottom:2px;">Date</p>
            <p style="font-weight:700;color:#0f172a;font-size:11px;">${invoiceDate}</p>
          </div>
          ${echeanceDate ? `
          <div>
            <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;margin-bottom:2px;">Échéance</p>
            <p style="font-weight:700;color:#0f172a;font-size:11px;">${echeanceDate}</p>
          </div>` : ''}
          ${snapshot.enable_cover_count && coverCount ? `
          <div>
            <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;margin-bottom:2px;">Couverts</p>
            <p style="font-weight:700;color:#0f172a;font-size:11px;">${coverCount}</p>
          </div>` : ''}
        </div>
      </div>
    </div>
  </div>

  <!-- DESTINATAIRE -->
  ${clientHasAddress ? `
  <div style="margin-bottom:24px;display:flex;justify-content:flex-end;">
    <div style="width:50%;padding:16px;border-radius:12px;border:2px solid #f8fafc;background:white;box-shadow:0 1px 3px rgba(0,0,0,0.06);">
      <h3 style="font-size:9px;font-weight:900;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:8px;color:${accent};opacity:0.7;">Destinataire</h3>
      <p style="font-weight:700;font-size:18px;color:#0f172a;margin-bottom:2px;">${clientName || '-'}</p>
      <div style="color:#475569;font-size:11px;line-height:1.6;">
        <p>${clientAddress || '-'}</p>
        <p>${[clientPostalCode, clientCity, clientCountry].filter(Boolean).join(' ') || '-'}</p>
        ${payload.clientType === 'PROFESSIONNEL' && (payload.clientSiret || payload.clientVatNumber) ? `<p style="margin-top:4px;">${[payload.clientSiret ? `SIRET ${payload.clientSiret}` : '', payload.clientVatNumber ? `TVA ${payload.clientVatNumber}` : ''].filter(Boolean).join(' • ')}</p>` : ''}
        ${payload.deliveryAddress ? `<p style="margin-top:4px;">Livraison : ${payload.deliveryAddress}</p>` : ''}
        ${payload.purchaseOrderRef ? `<p style="margin-top:4px;">Réf. commande : ${payload.purchaseOrderRef}</p>` : ''}
        ${operationNatureLabel ? `<p style="margin-top:4px;">Nature de l'opération : ${operationNatureLabel}</p>` : ''}
        ${snapshot.enable_cover_count && coverCount ? `<p style="margin-top:4px;font-weight:600;">Nombre de couverts : ${coverCount}</p>` : ''}
      </div>
    </div>
  </div>` : ''}

  <!-- TABLE -->
  <table style="width:100%;margin-bottom:24px;border-collapse:collapse;">
    <thead>
      <tr style="border-bottom:2px solid #0f172a;text-align:left;">
        <th style="padding-bottom:8px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;">Désignation de la prestation</th>
        <th style="padding-bottom:8px;padding-left:8px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;text-align:center;">Qté</th>
        <th style="padding-bottom:8px;padding-left:8px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;text-align:right;">P.U. HT</th>
        <th style="padding-bottom:8px;padding-left:8px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;text-align:right;">P.U. TTC</th>
        ${headerCols}
        <th style="padding-bottom:8px;padding-left:12px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;text-align:right;">Total HT</th>
        <th style="padding-bottom:8px;padding-left:12px;font-size:10px;font-weight:900;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;text-align:right;">Total TTC</th>
      </tr>
    </thead>
    <tbody style="border-top:1px solid #f1f5f9;">
      ${lineRows}
    </tbody>
  </table>

  <!-- BOTTOM BLOCK : ancré en bas de la page où il atterrit (2026-09-05, Emeni), cf. le calcul de marge
       ajouté côté JS. -->
  <div class="pdf-anchor-bottom" style="margin-top:16px;">
    <!-- SUMMARY -->
    <div style="display:flex;justify-content:flex-end;margin-bottom:16px;">
      <div style="width:320px;background:#f8fafc;padding:20px;border-radius:16px;border:1px solid #f1f5f9;box-shadow:0 1px 3px rgba(0,0,0,0.06);">
        <div style="display:flex;justify-content:space-between;font-size:11px;color:#64748b;">
          <span>Sous-total Hors Taxes :</span>
          <span style="color:#0f172a;">${fmt(totalHt)}</span>
        </div>

        <div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(226,232,240,0.6);">
          ${tvaRows}
        </div>

        <div style="display:flex;justify-content:space-between;font-size:10px;font-weight:900;text-transform:uppercase;color:#475569;margin-top:4px;">
          <span>Total TVA cumulé :</span>
          <span>${fmt(totalTva)}</span>
        </div>

        <div style="display:flex;justify-content:space-between;font-size:10px;font-weight:900;text-transform:uppercase;color:#475569;margin-top:4px;padding-top:4px;border-top:1px solid rgba(226,232,240,0.6);">
          <span>Total TTC :</span>
          <span>${fmt(totalTtcBrut)}</span>
        </div>

        ${!isSettled && alreadyPaid > 0 ? `
        <div style="display:flex;justify-content:space-between;font-size:10px;font-weight:900;text-transform:uppercase;color:#475569;margin-top:4px;">
          <span>Déjà réglé${payload.paymentMethod ? ` (${payload.paymentMethod})` : ''} :</span>
          <span>- ${fmt(alreadyPaid)}</span>
        </div>` : ''}

        <div style="padding-top:16px;margin-top:4px;border-top:2px solid ${bottomColor};display:flex;justify-content:space-between;align-items:baseline;">
          <span style="font-size:10px;font-weight:900;text-transform:uppercase;letter-spacing:0.1em;color:${bottomColor};opacity:${isSettled ? 0.7 : 1};">${bottomLabel}</span>
          <span style="font-size:24px;font-weight:900;color:${bottomColor};">${fmt(bottomAmount)}</span>
        </div>

        ${paymentMethodLines}

        ${netToPay > 0 && payload.dueDate ? `
        <div style="text-align:right;font-size:10px;font-weight:700;color:#dc2626;margin-top:4px;">
          À régler avant le ${toDisplayDate(payload.dueDate)}
        </div>` : ''}

      </div>
    </div>

    <!-- LEGAL FOOTER -->
    <div style="margin-top:16px;padding-top:16px;border-top:1px solid #f1f5f9;text-align:center;">
      <div>
        <p style="font-size:10px;color:#64748b;font-weight:900;text-transform:uppercase;letter-spacing:0.2em;margin-bottom:4px;">${companyName}</p>
        ${legalParts1.length > 0 ? `
        <div style="font-size:9.5px;color:#475569;line-height:1.6;font-weight:500;text-transform:uppercase;letter-spacing:0.04em;">
          ${legalParts1.join(' ')}
        </div>` : ''}
        ${legalParts2.length > 0 ? `
        <div style="font-size:9.5px;color:#475569;line-height:1.6;font-weight:500;text-transform:uppercase;letter-spacing:0.04em;">
          ${legalParts2.join(' ')}
        </div>` : ''}
        <div style="font-size:9.5px;color:#475569;line-height:1.6;font-weight:500;text-transform:uppercase;letter-spacing:0.04em;">
          ${legalParts3.join(' ')}
        </div>
      </div>
    </div>
  </div>

</div>
</body>
</html>`;
}

/**
 * Génère un PDF depuis les données d'une facture, en rendant le même template
 * HTML que l'aperçu de CreateInvoice.tsx.
 */
export async function generateInvoicePdfFromData(params: {
    invoice: any;
    payload: any;
    snapshot: any;
    accentColor?: string;
}): Promise<{ pdf: any; blob: Blob }> {
    exigerMentionsLegales('facture', params.snapshot);
    // Logo incorporé avant la capture (cf. lib/pdfTelephone.ts) : sur téléphone, un chargement réseau tardif le faisait disparaître.
    const logoIncorpore = await logoEnDataUri(params.snapshot?.logo_url ? resolveLogoUrl(params.snapshot.logo_url) : null);
    const htmlContent = buildInvoiceHtml(logoIncorpore ? { ...params, snapshot: { ...params.snapshot, logo_url: logoIncorpore } } : params);

    // Crée un iframe caché pour rendre le HTML
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '210mm';
    // Assez haut pour ne jamais brider une facture à beaucoup de lignes (contenu réel mesuré ensuite via
    // scrollHeight, pas cette valeur) — une facture classique d'une page n'en est pas affectée.
    iframe.style.height = '2000mm';
    iframe.style.border = 'none';
    iframe.style.zIndex = '-1';
    document.body.appendChild(iframe);

    await new Promise<void>((resolve) => {
        iframe.onload = () => resolve();
        iframe.srcdoc = htmlContent;
        // Fallback timeout
        setTimeout(resolve, 2000);
    });

    // Attendre que les images soient chargées
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (iframeDoc) {
        const imgs = Array.from(iframeDoc.querySelectorAll('img'));
        if (imgs.length > 0) {
            await Promise.all(imgs.map((img) =>
                new Promise<void>((res) => {
                    if (img.complete) { res(); return; }
                    img.onload = () => res();
                    img.onerror = () => res();
                    setTimeout(res, 1500);
                })
            ));
        }
        // BUG TROUVÉ (2026-09-05, Emeni : « le logo ne s'affiche pas sur iPhone », sur le devis avec le même
        // mécanisme) : computeBoundedLogoSize ne sait lire que les PNG (chunk IHDR) — pour tout autre format
        // (JPEG...), le logo retombe sur max-height/max-width + object-fit:contain, que Safari iOS ne
        // restitue pas de façon fiable dans le <foreignObject> SVG de html-to-image. Filet de sécurité :
        // si l'image logo n'a pas de largeur/hauteur explicites en pixels (donc encore sur object-fit), on
        // les calcule ici depuis ses dimensions réelles une fois chargée — fonctionne quel que soit le format.
        const logoImgEl = iframeDoc.querySelector<HTMLImageElement>('img[alt="Logo"]');
        if (logoImgEl && !logoImgEl.style.width && logoImgEl.naturalWidth && logoImgEl.naturalHeight) {
            const maxWidth = 250, maxHeight = 112;
            const ratio = logoImgEl.naturalWidth / logoImgEl.naturalHeight;
            let width = maxWidth, height = maxWidth / ratio;
            if (height > maxHeight) { height = maxHeight; width = maxHeight * ratio; }
            logoImgEl.style.width = `${Math.round(width)}px`;
            logoImgEl.style.height = `${Math.round(height)}px`;
            logoImgEl.style.maxWidth = '';
            logoImgEl.style.maxHeight = '';
            logoImgEl.style.objectFit = '';
        }
    }

    const targetEl = iframeDoc?.body?.firstElementChild as HTMLElement | null;
    if (!targetEl) {
        document.body.removeChild(iframe);
        throw new Error('Impossible de rendre la facture.');
    }

    // Attend que les polices de l'iframe soient chargées AVANT de mesurer offsetTop/offsetHeight — sinon la
    // police de repli (plus large/étroite) est utilisée pour la mesure, décalant les zones interdites par
    // rapport à la position réelle dans l'image capturée par toPng() juste après (même correctif que
    // devisPrestationPdfGenerator.ts).
    if (iframe.contentWindow && (iframe.contentWindow as Window & { document: Document }).document.fonts) {
        try { await (iframe.contentWindow as Window & { document: Document }).document.fonts.ready; } catch { /* ignore */ }
    }

    // Zones interdites à la découpe — même logique que le devis Prestation (demande d'Emeni, 2026-09-05) :
    // une facture à beaucoup de lignes doit désormais passer sur une deuxième page plutôt que de tout
    // rétrécir sur une seule, sans jamais couper une ligne d'article en deux entre deux pages.
    const cssScale = 210 / targetEl.scrollWidth;
    const buildForbiddenEls = () => [
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('table')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('tr')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('p')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('li')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('.pdf-anchor-bottom')),
    ];
    let forbiddenElsRaw = buildForbiddenEls();
    let targetRectTop = targetEl.getBoundingClientRect().top;
    const MAX_ATOMIC_BLOCK_HEIGHT_MM = 297 - 8 - 8;
    const computeForbiddenRanges = () => forbiddenElsRaw.map((el) => {
        const rect = el.getBoundingClientRect();
        return { top: (rect.top - targetRectTop) * cssScale, bottom: (rect.bottom - targetRectTop) * cssScale, tag: el.tagName.toLowerCase() };
    }).filter((r) => (r.bottom - r.top) <= MAX_ATOMIC_BLOCK_HEIGHT_MM);
    let forbiddenRanges = computeForbiddenRanges();
    const headingKeepWithNext = Array.from(targetEl.querySelectorAll<HTMLElement>('h1, h2, h3')).map((h) => {
        const rect = h.getBoundingClientRect();
        const top = (rect.top - targetRectTop) * cssScale;
        let bottom = (rect.bottom - targetRectTop) * cssScale;
        const next = h.nextElementSibling as HTMLElement | null;
        if (next) {
            const nextRect = next.getBoundingClientRect();
            bottom = Math.max(bottom, (nextRect.top - targetRectTop) * cssScale + 8);
        }
        return { top, bottom, tag: 'h' };
    });
    forbiddenRanges.push(...headingKeepWithNext);

    // Ancrage en bas de page (2026-09-05, Emeni), même logique que devisPrestationPdfGenerator.ts : simule
    // la pagination pour trouver sur quelle page le bloc total+légal atterrit, puis le pousse en bas de
    // CETTE page via un margin-top.
    const anchorEl = targetEl.querySelector<HTMLElement>('.pdf-anchor-bottom');
    if (anchorEl) {
        const anchorRect = anchorEl.getBoundingClientRect();
        const anchorTop = (anchorRect.top - targetRectTop) * cssScale;
        const anchorHeight = (anchorRect.bottom - anchorRect.top) * cssScale;
        const rangesAboveAnchor = forbiddenRanges.filter((r) => r.top < anchorTop - 0.5);
        const SIM_PAGE_HEIGHT_MM = 297, SIM_CONTINUATION_TOP_MM = 8, SIM_BOTTOM_MM = 8;
        let simUsedHeight = 0, simFirstPage = true, pageStart = 0, pageBudgetAtLanding = 0;
        while (true) {
            const pageBudgetSim = (simFirstPage ? SIM_PAGE_HEIGHT_MM : SIM_PAGE_HEIGHT_MM - SIM_CONTINUATION_TOP_MM) - SIM_BOTTOM_MM;
            const naiveBreakSim = simUsedHeight + pageBudgetSim;
            if (naiveBreakSim >= anchorTop) { pageStart = simUsedHeight; pageBudgetAtLanding = pageBudgetSim; break; }
            let breakAtSim = naiveBreakSim;
            for (const r of rangesAboveAnchor) {
                if (naiveBreakSim > r.top && naiveBreakSim < r.bottom && r.top > simUsedHeight + 1 && r.top < breakAtSim) {
                    if (r.tag === 'table' && (r.top - simUsedHeight) > 60) continue;
                    breakAtSim = r.top;
                }
            }
            if (breakAtSim <= simUsedHeight) breakAtSim = simUsedHeight + pageBudgetSim;
            simUsedHeight = breakAtSim;
            simFirstPage = false;
        }
        const pageBottomLimit = pageStart + pageBudgetAtLanding;
        const spacerMm = pageBottomLimit - anchorTop - anchorHeight;
        if (spacerMm > 0.5) {
            anchorEl.style.marginTop = `${spacerMm / cssScale}px`;
            targetRectTop = targetEl.getBoundingClientRect().top;
            forbiddenElsRaw = buildForbiddenEls();
            forbiddenRanges = computeForbiddenRanges();
            forbiddenRanges.push(...headingKeepWithNext);
        }
    }

    try {
        // Ratio de capture plafonné pour rester sous la limite de taille de canvas des navigateurs mobiles
        // (même correctif que contractPdfGenerator.ts / devisPrestationPdfGenerator.ts).
        const safePixelRatio = Math.min(1.5, 4000 / Math.max(targetEl.scrollWidth, targetEl.scrollHeight, 1));
        const imgData = await capturerAvecLogo(toPng, targetEl, {
            cacheBust: true,
            pixelRatio: safePixelRatio,
            backgroundColor: '#ffffff',
            width: targetEl.scrollWidth,
            height: targetEl.scrollHeight,
        });

        const { jsPDF } = await import('jspdf');
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageWidth = 210;
        const pageHeight = 297;

        const imageProps = pdf.getImageProperties(imgData);
        // Mise à l'échelle sur la LARGEUR seulement (jamais sur la hauteur, qui compresserait tout sur une
        // seule page), puis découpage vertical page par page — comme le devis.
        const scale = pageWidth / imageProps.width;
        const imageWidth = imageProps.width * scale;
        const imageHeight = imageProps.height * scale;

        // Chaque page utilise une image RECADRÉE à sa tranche exacte (via un canvas), jamais l'image
        // complète repositionnée — sinon jsPDF repeint le contenu entre le point de coupure et le bas de
        // la page précédente une deuxième fois (bug déjà corrigé sur le devis).
        const sourceImg = new Image();
        await new Promise<void>((resolve, reject) => { sourceImg.onload = () => resolve(); sourceImg.onerror = () => reject(new Error('Image de rendu invalide.')); sourceImg.src = imgData; });
        const pxPerMm = imageProps.width / imageWidth;

        // Seule la première page a une marge en haut (incluse dans le contenu capturé, padding:8mm du
        // conteneur) — les pages suivantes démarrent une tranche recadrée en plein milieu du document, sans
        // aucune marge : on réserve donc un blanc de 8mm en haut à partir de la 2e page, et en bas de chaque
        // page qui n'est pas la dernière.
        const CONTINUATION_TOP_MARGIN_MM = 8;
        const BOTTOM_MARGIN_MM = 8;
        let usedHeight = 0;
        let firstPage = true;
        while (usedHeight < imageHeight - 0.5) {
            const pageBudget = (firstPage ? pageHeight : pageHeight - CONTINUATION_TOP_MARGIN_MM) - BOTTOM_MARGIN_MM;
            const naiveBreak = Math.min(usedHeight + pageBudget, imageHeight);
            // 2026-11-04 : une page 2 entièrement blanche quand le contenu dépassait la page 1 de quelques
            // millimètres. Ce reliquat n'est que le blanc de marge bas du document : si tout le reste tient sur
            // la page en réservant au plus cette marge, il y reste (même correctif que le devis Prestation).
            const toutLeResteTient = (imageHeight - usedHeight) <= pageBudget + BOTTOM_MARGIN_MM;
            let breakAt = toutLeResteTient ? imageHeight : naiveBreak;
            for (const r of forbiddenRanges) {
                if (!toutLeResteTient && naiveBreak > r.top && naiveBreak < r.bottom && r.top > usedHeight + 1 && r.top < breakAt) {
                    // Même correctif que devisPrestationPdfGenerator.ts (2026-09-05, Emeni : « page 1 quasi
                    // vide ») : un <table> entier n'est reporté en bloc à la page suivante que si ça ne
                    // gâche pas plus de MAX_WASTED_SPACE_MM sur la page en cours, sinon on laisse la
                    // protection ligne par ligne (<tr>) faire le travail.
                    const MAX_WASTED_SPACE_MM = 60;
                    if (r.tag === 'table' && (r.top - usedHeight) > MAX_WASTED_SPACE_MM) continue;
                    breakAt = r.top;
                }
            }
            if (breakAt < naiveBreak) {
                const withMargin = breakAt - 4;
                if (withMargin > usedHeight + 1) breakAt = withMargin;
            }
            if (breakAt <= usedHeight) breakAt = usedHeight + pageBudget; // ligne plus haute qu'une page : coupe quand même, jamais de boucle infinie

            const sliceHeightMm = breakAt - usedHeight;
            const srcY = Math.round(usedHeight * pxPerMm);
            const srcH = Math.max(1, Math.round(sliceHeightMm * pxPerMm));
            const canvas = document.createElement('canvas');
            canvas.width = imageProps.width;
            canvas.height = srcH;
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(sourceImg, 0, srcY, imageProps.width, srcH, 0, 0, imageProps.width, srcH);
            const sliceData = canvas.toDataURL('image/png');

            if (!firstPage) pdf.addPage();
            pdf.addImage(sliceData, 'PNG', 0, firstPage ? 0 : CONTINUATION_TOP_MARGIN_MM, imageWidth, sliceHeightMm, undefined, 'SLOW');
            usedHeight = breakAt;
            firstPage = false;
        }

        const blob = pdf.output('blob');
        return { pdf, blob };
    } finally {
        document.body.removeChild(iframe);
    }
}
